import { type IdentifyProvider, IdentifyProviderEnum } from "@src/appV2/Auth/api/types";
import { type OtpRequestChannel, useRequestOtp } from "@src/appV2/Auth/api/useRequestOtp";
import { extractPhoneNumberDigits } from "@src/appV2/lib";
import { useCountdownTimer } from "@src/appV2/lib/utils/useCountdownTimer";
import constate from "constate";
import { useCallback, useState } from "react";

const DEFAULT_COUNTDOWN_DURATION_IN_SECONDS = 30;

interface StepUpAuthProps {
  countdownDurationInSeconds?: number;
}

function useStepUpAuth(props: StepUpAuthProps) {
  const { countdownDurationInSeconds = DEFAULT_COUNTDOWN_DURATION_IN_SECONDS } = props;

  const [identityProvider, setIdentityProvider] = useState<IdentifyProvider>(
    IdentifyProviderEnum.firebase
  );

  const { countdownIsLoading, startTimer, secondsLeft } = useCountdownTimer({
    durationInSeconds: countdownDurationInSeconds,
  });

  const { mutateAsync: requestOtpMutation, isLoading: isRequestingOtp } = useRequestOtp();

  const requestOtp = useCallback(
    async (params: { phone: string; isSignup: boolean; channel?: OtpRequestChannel }) => {
      const {
        data: { identityProvider: responseIdentityProvider },
      } = await requestOtpMutation({
        phone: extractPhoneNumberDigits(params.phone),
        isSignup: params.isSignup,
        channel: params.channel,
      });

      setIdentityProvider(responseIdentityProvider);

      return { identityProvider: responseIdentityProvider };
    },
    [requestOtpMutation]
  );

  return {
    countdownIsLoading,
    startCountdownTimer: startTimer,
    countdownSecondsLeft: secondsLeft,
    requestOtp,
    isRequestingOtp,
    identityProvider,
  };
}

export const [StepUpAuthProvider, useStepUpAuthContext] = constate(useStepUpAuth);
